import { AlertCircle, Download, ZoomIn } from "lucide-react";
import React, { useCallback, useEffect, useState } from "react";
import {
  ActionIcon,
  Alert,
  Button,
  Image,
  Modal,
  Tabs,
  Text,
} from "@mantine/core";
import { showNotification } from "@mantine/notifications";
import moment from "moment";

import {
  EmployeeTicketDetailsProps,
  QueryCategory,
  ModalModes,
  ModalActions,
  InboxQueryStatus,
} from "../utils/types";
import { downloadFile } from "../../../utils/File";
import QueryDetailsData from "./QueryDetailsData";
import QueryDetailsHistory from "./QueryDetailsHistory";
import SupportingDocuments from "./SupportingDocuments";
import {
  getQueryIcon,
  getTicketActionVisibility,
  isTicketAlreadyActionedError,
  makeOptions,
  TICKET_ALREADY_ACTIONED_MESSAGE,
} from "../utils/utils";
import { ConfirmResolveTicketModal } from "./ConfirmResolveTicketModal";
import FeatureFlags from "../../../common/feature-flagging/FeatureFlags";
import { useToasts } from "../../../providers/notifications/ToastsContext";
import useGetQueryOutcomesDataService from "../service/api/GetQueryOptions";
import { useInboxQueryResolveApiService } from "../service/api/InboxQueryApiService";
import { useEnabledFeatureFlag } from "../../../common/feature-flagging/FeatureFlagProvider";

const EmployeeTicketStyles = {
  resolveButton: {
    root: {
      "&:disabled": {
        backgroundColor: "transparent !important",
        borderColor: "#e9ecef !important",
      },
    },
  },
};

const EmployeeTicketDetails: React.FC<EmployeeTicketDetailsProps> = ({
  ticket,
  setTicket,
  setTicketId,
  inboxQueryTab,
}): JSX.Element => {
  const [activeTab, setActiveTab] = useState<number>(0);
  const [documentOpened, setDocumentOpened] = useState<boolean>(false);
  const [resolveModalOpened, setResolveModalOpened] = useState<boolean>(false);
  const [modalMode, setModalMode] = useState(ModalModes.Resolve);

  const { showToast } = useToasts();

  const {
    isError,
    isSuccess,
    isLoading: isResolving,
    error: resolveError,
    mutate: resolveDocumentQuery,
  } = useInboxQueryResolveApiService();

  useEffect(() => {
    if (isSuccess) {
      let messageId;

      // Determine the messageId based on the ticket category and modal mode
      if (ticket.category === QueryCategory.LEAVE) {
        // Handle leave queries
        if (modalMode === ModalModes.Approve) {
          // If the modal mode is Approve, check if the user is the final approver
          messageId = ticket.final_approver
            ? "leave_query_resolved_final_approver" // Final approver message
            : "leave_query_resolved"; // Non-final approver message
        } else if (modalMode === ModalModes.Decline) {
          // If the modal mode is Decline
          messageId = "leave_query_declined";
        } else if (modalMode === ModalModes.Cancel) {
          // If the modal mode is Cancel
          messageId = "leave_query_cancelled";
        }
      } else {
        // Handle non-leave queries (default case)
        messageId = "document_query_resolved";
      }

      showToast({ strapiId: messageId });
      setTicketId(null);
      setTicket(null);
    }
  }, [isSuccess]);

  useEffect(() => {
    if (isError) {
      // ENG-5230: the monolith now returns 409 TICKET_ALREADY_ACTIONED when the leave request has
      // already been actioned or advanced to the next approver (a co-approver got there first).
      // That's an expected race rather than a failure, so surface friendly copy and skip the
      // generic resolve-error toast. onError has already refetched the list/detail, so the stale
      // Approve/Decline control disappears on its own. Every other error keeps current behaviour.
      if (isTicketAlreadyActionedError(resolveError)) {
        showNotification({
          message: TICKET_ALREADY_ACTIONED_MESSAGE,
          color: "yellow",
        });
        return;
      }

      let messageId;

      // Determine the messageId based on the ticket category and modal mode
      if (ticket.category === QueryCategory.LEAVE) {
        // Handle leave queries
        if (modalMode === ModalModes.Approve) {
          // If the modal mode is Approve and the action failed
          messageId = "leave_request_approve_failed";
        } else if (modalMode === ModalModes.Decline) {
          // If the modal mode is Decline and the action failed
          messageId = "leave_request_decline_failed";
        } else if (modalMode === ModalModes.Cancel) {
          // If the modal mode is Decline and the action failed
          messageId = "leave_request_cancel_failed";
        }
      } else {
        // Handle non-leave queries (default case)
        messageId = "document_query_resolve_error";
      }

      showToast({ strapiId: messageId });
    }
  }, [isError]);

  const inboxResolutionDataEnabled = useEnabledFeatureFlag(
    FeatureFlags.QUERY_INBOX_RESOLUTION_OUTCOME_DATA_ENABLED
  );
  // Enable the Resolution data form if the feature flag is enabled and the modal mode is Decline, Resolve, or Cancel
  const modalResolutionDataEnabled =
    inboxResolutionDataEnabled &&
    (modalMode === ModalModes.Decline ||
      modalMode === ModalModes.Resolve ||
      modalMode === ModalModes.Cancel);
  const sendEmployeeNotificationEnabled = useEnabledFeatureFlag(
    FeatureFlags.EMPLOYEE_TICKETING_SERVICE_NOTIFY_EMPLOYEE
  );

  const { data: queryOutcomes } = useGetQueryOutcomesDataService({
    category: ticket.category,
  });

  const queryOutcomeOptions = queryOutcomes?.data
    ? makeOptions(
        queryOutcomes.data.map((queryOutcome) => queryOutcome.attributes)
      )
    : [];

  const mapToStatus = (mode: ModalModes): InboxQueryStatus => {
    switch (mode) {
      case ModalModes.Approve:
        return InboxQueryStatus.APPROVED;
      case ModalModes.Resolve:
        return InboxQueryStatus.RESOLVED;
      case ModalModes.Decline:
        return InboxQueryStatus.DECLINED;
      case ModalModes.Cancel:
        return InboxQueryStatus.CANCELLED;
      default:
        return InboxQueryStatus.UNDEFINED;
    }
  };

  const resolveTicket = (
    queryOutcome?: string,
    employeeMessage?: string
  ): void => {
    const payload = {
      query_outcome: queryOutcome,
      status: mapToStatus(modalMode),
      message_to_employee: employeeMessage,
      resolve: ticket.id ?? ticket.query_id,
      queryResolutionDataEnabled: inboxResolutionDataEnabled, // Determine if the API should send the query resolution data
    };

    resolveDocumentQuery(payload);
  };

  const ticketTitle = useCallback((): string => {
    let queryTypeText = "a query";

    if (ticket.category === QueryCategory.LEAVE) {
      queryTypeText = "a leave request";
    }

    return `${ticket.employee.first_name} ${ticket.employee.last_name} (${ticket?.employee.employee_no}) submitted ${queryTypeText}`;
  }, [ticket]);

  const renderActions = (): JSX.Element => {
    // Whether the Approve/Decline controls should be hidden/disabled. This folds in the ticket's
    // terminal status, the "Reviewed" tab rule, an in-flight resolve, and — crucially for ENG-5210 —
    // whether the backend still considers this user the current approver (`can_action`). See
    // getTicketActionVisibility for the full rationale.
    const { hidden: CTAHidden, disabled: CTADisabled } =
      getTicketActionVisibility(ticket, inboxQueryTab, isResolving);

    const ifShowCancelButton =
      InboxQueryStatus.APPROVED === ticket.status &&
      ticket.final_approver === true;
    const disabledCancelCTA =
      isResolving || ticket.status === InboxQueryStatus.CANCELLED;

    if (ticket.category === QueryCategory.LEAVE) {
      return (
        <span style={{ display: "flex", gap: "8px" }}>
          <Button
            size="xs"
            variant="outline"
            disabled={CTADisabled}
            hidden={CTAHidden}
            onClick={() => {
              setResolveModalOpened(true);
              setModalMode(ModalModes.Approve);
            }}
            loading={isResolving}
            styles={EmployeeTicketStyles.resolveButton}
          >
            {ModalActions.Approve}
          </Button>
          <Button
            size="xs"
            variant="outline"
            disabled={CTADisabled}
            hidden={CTAHidden}
            onClick={() => {
              setResolveModalOpened(true);
              setModalMode(ModalModes.Decline);
            }}
            loading={isResolving}
            styles={EmployeeTicketStyles.resolveButton}
          >
            {ModalActions.Decline}
          </Button>

          {/* Render the Cancel button if the ticket is approved & viewing as the Payroll Approver */}
          {ifShowCancelButton && (
            <Button
              size="xs"
              variant="outline"
              disabled={disabledCancelCTA}
              onClick={() => {
                setResolveModalOpened(true);
                setModalMode(ModalModes.Cancel);
              }}
              loading={isResolving}
              styles={EmployeeTicketStyles.resolveButton}
            >
              {ModalActions.Cancel} Leave
            </Button>
          )}
        </span>
      );
    }

    return (
      <Button
        size="xs"
        variant="outline"
        disabled={CTADisabled}
        hidden={CTAHidden}
        onClick={() => {
          setResolveModalOpened(true);
          setModalMode(ModalModes.Resolve);
        }}
        loading={isResolving}
        styles={EmployeeTicketStyles.resolveButton}
      >
        {ModalActions.Resolve}
      </Button>
    );
  };

  const renderSupportingDocs = (): JSX.Element => {
    let supportingDocs;
    if (ticket.file_query_proof.length > 0) {
      supportingDocs = ticket.file_query_proof;
    } else if (ticket.file_leave_proof.length > 0) {
      supportingDocs = ticket.file_leave_proof;
    }

    return supportingDocs ? (
      <Tabs.Tab label="Supporting Documents">
        <SupportingDocuments
          supportingDocuments={supportingDocs} // Returns a merged array of both file_query_proof and file_other_query_proof files
          ticket={ticket}
        />
      </Tabs.Tab>
    ) : (
      <span />
    );
  };

  const getModalSettings = (
    mode: ModalModes,
    finalApprover: boolean
  ): { title: string; description: string } => {
    let title;
    let description;

    switch (mode) {
      case ModalModes.Approve:
        title = "Approve this leave request?";
        description = finalApprover
          ? "A notification will be sent to the employee to let them know that their leave request has been approved."
          : "This leave request will move onto the next reviewer for approval.";
        break;
      case ModalModes.Decline:
        title = "Decline this leave request?";
        description =
          "A notification will be sent to the employee to let them know that their leave request was declined.";
        break;
      case ModalModes.Cancel:
        title = "Cancel this approved leave request?";
        description =
          "A notification will be sent to the employee to inform them that their leave request has been cancelled. This request will remain in your ‘Reviewed’ tab and the status will change to “Cancelled.”";
        break;
      default:
        title = "Resolve this query?";
        description = sendEmployeeNotificationEnabled
          ? 'A notification will be sent to the employee and this request will move into your "Reviewed" tab.'
          : 'This will close out the query and move it into the "Reviewed" tab. You cannot undo this action.';
        break;
    }

    return { title, description };
  };

  const getModalTitle = (mode: ModalModes): string => {
    const { title } = getModalSettings(mode, !!ticket.final_approver);
    return title;
  };

  const getModalDescription = (mode: ModalModes): string => {
    const { description } = getModalSettings(mode, !!ticket.final_approver);
    return description;
  };

  const getModalConfirmText = (mode: ModalModes): string => {
    let confirmText;
    switch (mode) {
      case ModalModes.Approve:
        confirmText = "Approve";
        break;
      case ModalModes.Decline:
        confirmText = "Decline";
        break;
      case ModalModes.Resolve:
        confirmText = "Resolve";
        break;
      case ModalModes.Cancel:
        confirmText = "Cancel Approval";
        break;
      default:
        confirmText = "Resolve";
        break;
    }

    return confirmText;
  };

  const getModalQueryOutcome = useCallback(
    () =>
      ticket?.category === QueryCategory.LEAVE ? "Reason" : "Outcome of query",
    [ticket]
  );

  return (
    <Tabs
      active={activeTab}
      onTabChange={setActiveTab}
      classNames={{ body: "h-full overflow-y-auto", root: "flex flex-col" }}
      className="flex-1"
    >
      <Tabs.Tab label="Details">
        <div className="sticky top-0 bottom-0 flex flex-col gap-6 px-4 py-2">
          {/* Ticket status & CTA section */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              width: "100%",
            }}
          >
            <span
              style={{
                display: "flex",
                alignItems: "center",
              }}
            >
              <Image
                src={getQueryIcon(ticket.query_type, ticket.category)}
                alt={`${ticket.query_type} Icon`}
                title={`${ticket.query_type} Icon`}
                width={24}
                height={24}
              />
              <h3
                className="capitalize font-bold text-jem-primary-base grow"
                style={{
                  fontSize: "12px",
                  lineHeight: "16px",
                  paddingLeft: "8px",
                  fontFamily: "Inter",
                }}
              >
                {ticket.category === QueryCategory.LEAVE
                  ? `${ticket.query_type}`
                  : `${ticket.query_type} Query`}
              </h3>
            </span>

            <span
              style={{
                display: "flex",
                alignItems: "center",
              }}
            >
              <span
                className={`px-3 h-6 flex items-center rounded-full ${
                  ticket.status === "OPEN"
                    ? "text-[#14926D] bg-[#E8FAF5]"
                    : "bg-[#E1E1E1] text-[#384D5C]"
                } text-xs capitalize mr-4`}
              >
                {ticket.status.toLowerCase()}
              </span>
              {renderActions()}
            </span>
          </div>
          {/* Employee details section */}
          <h3 className="text-xl font-bold text-jem-primary-base">
            {ticketTitle()}
          </h3>
          <div className="grid grid-cols-2 gap-4 pb-6 border-b border-jem-shade">
            <div className="flex flex-col">
              <span className="text-xs text-jem-primary-50t">Branch</span>
              <span className="text-[16px] text-jem-primary-base">
                {ticket.employee.employee_branch?.name || "No branch"}
              </span>
            </div>
            <div className="flex flex-col">
              <span className="text-xs text-jem-primary-50t">Division</span>
              <span className="text-[16px] text-jem-primary-base">
                {ticket.employee.employee_division?.name ?? "No division"}
              </span>
            </div>
            <div className="flex flex-col">
              <span className="text-xs text-jem-primary-50t">ID number</span>
              <span className="text-[16px] text-jem-primary-base">
                {ticket.employee.id_number}
              </span>
            </div>
            <div className="flex flex-col">
              <span className="text-xs text-jem-primary-50t">Phone Number</span>
              <span className="text-[16px] text-jem-primary-base">
                +{ticket.employee.mobile_number}
              </span>
            </div>
          </div>
          {/* Ticket details section */}
          <div className="flex flex-col gap-6">
            <QueryDetailsData ticket={ticket} />
            {ticket.history && ticket.history.length > 0 && (
              <QueryDetailsHistory ticket={ticket} />
            )}
          </div>
        </div>
      </Tabs.Tab>
      {/* Ticket document Tab; renders a link to the document being queried */}
      {ticket.document_asset_link && (
        <Tabs.Tab label={ticket.query_type}>
          <div className="flex flex-col h-full max-h-full">
            <object
              className="w-full grow"
              style={{ flex: "1 1 auto", minHeight: "0" }}
              data={`${ticket.document_asset_link}#toolbar=0&navpanes=0&scrollbar=0&view=FitH&&zoom=100%`}
              type="application/pdf"
            >
              <p>
                Alternative text - include a link{" "}
                <a
                  href={`${ticket.document_asset_link}#toolbar=0&navpanes=0&scrollbar=0&view=FitH&zoom=100%`}
                >
                  to the PDF!
                </a>
              </p>
            </object>
            <Modal
              size="80%"
              opened={documentOpened}
              onClose={() => setDocumentOpened(false)}
              title={ticket.query_type}
              classNames={{
                modal: "h-full",
                body: "h-full pb-24",
                inner: "h-full",
              }}
            >
              <object
                className="w-full h-full"
                data={`${ticket.document_asset_link}#toolbar=0&navpanes=0&scrollbar=0&view=FitH&&zoom=100%`}
                type="application/pdf"
              >
                <p>
                  Alternative text - include a link{" "}
                  <a
                    href={`${ticket.document_asset_link}#toolbar=0&navpanes=0&scrollbar=0&view=FitH&zoom=100%`}
                  >
                    to the PDF!
                  </a>
                </p>
              </object>
            </Modal>
            {ticket.document_changed_since_query && (
              <div className="flex items-center justify-start px-4 pt-4 border-t border-jem-shade">
                <Alert
                  icon={
                    <AlertCircle
                      size={20}
                      style={{
                        color: "#062133",
                      }}
                    />
                  }
                  classNames={{ wrapper: "flex items-center justify-start" }}
                  style={{
                    flexGrow: 0,
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    alignItems: "stretch",
                    gap: "8px",
                    padding: "16px",
                    borderRadius: "8px",
                    backgroundColor: "#FFF4EA",
                    borderColor: "#FF922B",
                  }}
                >
                  <Text
                    style={{
                      flexGrow: 0,
                      fontFamily: "Inter",
                      fontSize: "12px",
                      fontWeight: 400,
                      fontStretch: "normal",
                      fontStyle: "normal",
                      lineHeight: "16px",
                      letterSpacing: "0.16px",
                      textAlign: "left",
                      color: "#062133",
                    }}
                  >
                    <b>This document has been updated.</b> The displayed
                    document is what the employee viewed when querying.
                  </Text>
                </Alert>
              </div>
            )}
            <div
              className={`flex items-center justify-between px-4 pt-4 pb-8 ${
                !ticket.document_changed_since_query
                  ? "border-t border-jem-shade"
                  : ""
              }`}
            >
              <div className="text-xs text-jem-primary-50t">{`Uploaded on ${moment(
                ticket.document_uploaded_at
              ).format("DD MMMM YYYY [at] HH:mm")} by ${
                ticket.document_uploaded_by
                  ? `${ticket.document_uploaded_by.first_name} ${ticket.document_uploaded_by.last_name}`
                  : "Admin User"
              }`}</div>
              <div className="flex gap-4">
                <ActionIcon
                  className="border rounded border-jem-primary-50t w"
                  onClick={() => downloadFile(ticket.document_asset_link)}
                  size={32}
                >
                  <Download size={16} className="text-jem-primary-base" />
                </ActionIcon>
                <ActionIcon
                  className="border rounded border-jem-primary-50t"
                  onClick={() => setDocumentOpened(true)}
                  size={32}
                >
                  <ZoomIn size={16} className="text-jem-primary-base" />
                </ActionIcon>
              </div>
            </div>
          </div>
        </Tabs.Tab>
      )}

      {/* 
        Legacy Supporting Document Tab
        Only shown if there's a single supporting document from the old chatbot system 
      */}
      {ticket.supporting_document_asset_link && (
        <Tabs.Tab label="Supporting Document">
          <SupportingDocuments
            supportingDocuments={[ticket.supporting_document_asset_link]}
            ticket={ticket}
          />
        </Tabs.Tab>
      )}
      {/* 
        WhatsApp Flow Supporting Documents Tab
        Shows multiple supporting documents collected through WhatsApp Flows
        Uses plural "Documents" in label to distinguish from legacy single document
      */}
      {renderSupportingDocs()}

      {/* Render the ConfirmResolveTicketModal with the appropriate modal settings */}
      <ConfirmResolveTicketModal
        lang={{
          title: getModalTitle(modalMode),
          description: getModalDescription(modalMode),
          confirm: getModalConfirmText(modalMode),
          cancel: "Cancel",
          queryOutcome: getModalQueryOutcome(),
          employeeMessage: "Message to employee",
        }}
        isOpen={resolveModalOpened}
        resolveTicket={resolveTicket}
        setOpen={setResolveModalOpened}
        queryOutcomeOptions={queryOutcomeOptions}
        queryResolutionDataEnabled={modalResolutionDataEnabled} // Determines if the modal should show the query resolution form
      />
    </Tabs>
  );
};
export default EmployeeTicketDetails;
